'use client';

import { type JSX, useState } from 'react';

import { Badge, Text } from '@videoblocks/design-system';

import { useChatSessionContext } from '../../_context/SessionContext';
import { useSearchResults } from '../../_hooks';
import { UsageLimitBanner } from '../browse-limit';
import { HelperCardCarousel } from '../helper-cards';
import { AudioResultsGrid } from './AudioResultsGrid';
import { ResultTypeToggle, type ResultContentType } from './ResultTypeToggle';
import { VideoResultsGrid } from './VideoResultsGrid';
import { VideoResultsGridSkeleton } from './VideoResultsGridSkeleton';

export function ResultsPane(): JSX.Element {
    const { results, totalCount, isSearching, hasSearched } = useSearchResults();
    const { hasReachedLimit, supportsAudio, currentTitle } = useChatSessionContext();
    const [selectedType, setSelectedType] = useState<ResultContentType>('video');

    const audioResults = results.filter((r) => r.contentClass === 'audio');
    const videoResults = results.filter((r) => r.contentClass !== 'audio');
    const showAudio = supportsAudio && audioResults.length > 0;
    const showVideo = videoResults.length > 0;
    const hasBothTypes = showAudio && showVideo;
    const showToggle = supportsAudio;
    const activeType: ResultContentType = hasBothTypes ? selectedType : showAudio ? 'audio' : 'video';

    function renderContent(): JSX.Element {
        if (isSearching) return <VideoResultsGridSkeleton />;
        if (hasSearched && (showAudio || showVideo)) {
            return activeType === 'audio' ? (
                <AudioResultsGrid audioItems={audioResults} />
            ) : (
                <VideoResultsGrid results={videoResults} />
            );
        }
        return <HelperCardCarousel />;
    }

    return (
        <div className="flex flex-col h-full">
            <div className="flex bg-white justify-between items-center border-b border-gray-200 h-[--spacing(14.25)]">
                <div className="px-6 py-2 text-left">
                    <Text fontSize="base" fontWeight="semibold" className="leading-tight">
                        {currentTitle}
                    </Text>
                    <Text fontSize="sm" fontWeight="regular" className="text-gray-500">
                        Top {hasSearched ? totalCount : 0} Displayed
                    </Text>
                </div>
                <div className="flex items-center gap-4 pr-6">
                    <Badge text="Beta" textCase="uppercase" shape="square" fontWeight="semibold" className="mt-1" />
                    {showToggle && (
                        <ResultTypeToggle
                            value={activeType}
                            onChange={setSelectedType}
                            videoCount={videoResults.length}
                            audioCount={audioResults.length}
                        />
                    )}
                </div>
            </div>
            {hasReachedLimit && <UsageLimitBanner />}
            <div className="flex-1 overflow-y-auto">{renderContent()}</div>
        </div>
    );
}
